import axios from "axios";
import client from "./client";
import { getMe } from "./user";
import { deviceIdHeaders } from "../lib/deviceId";
import { userIdFromAccessToken } from "../lib/accessToken";
import type { DecisionIn } from "./consent";

export interface AuthToken {
  username: string;
  /** The user's id (access-token `sub`). */
  userId: string;
  hasInformation: boolean;
  /** A gate topic still needs an answer (Before you start / re-consent before the app). */
  consentPending?: boolean;
  /** No date of birth stored yet: the Date of birth screen comes first. */
  birthDatePending?: boolean;
}

export interface SignUpBody {
  email: string;
  password: string;
  rePassword: string;
  username: string;
}

export interface IamTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  consentPending?: boolean;
  birthDatePending?: boolean;
}

export interface ChallengeResponse {
  challengeId?: string;
  step: string;
  email?: string;
  name?: string;
  tokens?: IamTokenResponse;
}

/** Sign-in refused: neither the token's `sub` nor `/api/user/me` gave a user id. */
export class NoUserIdError extends Error {
  constructor() {
    super("Sign-in failed. Please try again.");
    this.name = "NoUserIdError";
  }
}

async function resolveAuthToken(tokens: IamTokenResponse): Promise<AuthToken> {
  const accessToken = tokens.access_token;
  let userId = userIdFromAccessToken(accessToken);

  let username = userId;
  let hasInformation = false;
  try {
    const me = await getMe();
    username = me.username;
    hasInformation = me.hasInformation ?? false;
    if (!userId) userId = me.id;
  } catch {
    // Profile fetch failed — the user id is used as fallback
  }
  if (!userId) throw new NoUserIdError();

  return {
    username,
    userId,
    hasInformation,
    consentPending: tokens.consentPending === true,
    birthDatePending: tokens.birthDatePending === true,
  };
}

export async function createChallenge(params: {
  type: "email" | "google";
  email?: string;
  googleIdToken?: string;
  captcha?: string;
}): Promise<ChallengeResponse> {
  const res = await client.post<ChallengeResponse>(
    "/iam/api/v1/auth/challenge",
    params,
  );
  return res.data;
}

export async function verifyOTP(
  challengeId: string,
  code: string,
): Promise<{ challengeId: string; step: string }> {
  const res = await client.post<{ challengeId: string; step: string }>(
    `/iam/api/v1/auth/challenge/${challengeId}/otp`,
    { code },
  );
  return res.data;
}

/** What a new account sends with its password: the date of birth and the Before you start answers (source SIGN_UP). */
export interface RegistrationConsent {
  /** YYYY-MM-DD. Sent once with this request, never stored or logged in the browser. */
  birthDate: string;
  decisions: DecisionIn[];
}

/**
 * Creates the account (register challenge). With `registration`, the date of birth and the consent batch go with
 * it: iam creates the account, the date and the consent events in one transaction (422 UNDER_AGE: nothing stored).
 */
export async function challengeSetPassword(
  challengeId: string,
  password: string,
  registration?: RegistrationConsent,
): Promise<AuthToken> {
  const body = registration
    ? registration.decisions.length === 0
      ? { password, birthDate: registration.birthDate }
      : {
        password,
        birthDate: registration.birthDate,
        consent: {
          platform: "WEB",
          appVersion: __APP_VERSION__,
          decisions: registration.decisions,
        },
      }
    : { password };
  const res = await client.post<IamTokenResponse>(
    `/iam/api/v1/auth/challenge/${challengeId}/set-password`,
    body,
  );
  return resolveAuthToken(res.data);
}

export async function challengePassword(
  challengeId: string,
  password: string,
): Promise<AuthToken> {
  const res = await client.post<IamTokenResponse>(
    `/iam/api/v1/auth/challenge/${challengeId}/password`,
    { password },
  );
  return resolveAuthToken(res.data);
}

export async function profileSetup(
  firstName: string,
  lastName: string,
  /** file-stc id of the uploaded (READY) avatar. */
  avatarFileId?: string,
): Promise<AuthToken> {
  // iam-stc sets the session cookies itself; its body has no `result` wrapper.
  const res = await client.post<
    { username: string; id: string } & Pick<IamTokenResponse, "consentPending" | "birthDatePending">
  >("/iam/api/v1/profile/setup", { firstName, lastName, avatarFileId });
  const { username, id: userId } = res.data;
  let hasInformation = false;
  try {
    const me = await getMe();
    hasInformation = me.hasInformation ?? false;
  } catch {
    hasInformation = true;
  }
  return {
    username,
    userId,
    hasInformation,
    consentPending: res.data.consentPending === true,
    birthDatePending: res.data.birthDatePending === true,
  };
}

/**
 * Stores the signed-in account's date of birth once (204). 403 UNDER_AGE: the account is deactivated and signed
 * out; 409 BIRTH_DATE_SET: already stored. The value is never echoed by the server.
 */
export async function setBirthDate(birthDate: string): Promise<void> {
  await client.put("/iam/api/v1/profile/birth-date", { birthDate });
}

export async function register(body: SignUpBody): Promise<void> {
  await client.post("/api/auth/sign-up", body);
}

export async function sendVerifyEmail(email: string): Promise<void> {
  await client.post("/api/auth/send-verify-email", null, { params: { email } });
}

export async function verifyEmail(
  email: string,
  verifyCode: string,
): Promise<void> {
  await client.post("/api/auth/verify-email", null, {
    params: { email, verifyCode },
  });
}

export async function checkExistEmail(email: string): Promise<boolean> {
  const res = await client.get<{ result: boolean }>(
    "/api/auth/check-exist-email",
    { params: { email } },
  );
  return res.data.result;
}

export async function checkExistUsername(username: string): Promise<boolean> {
  const res = await client.get<{ result: boolean }>(
    "/api/auth/check-exist-username",
    { params: { username } },
  );
  return res.data.result;
}

export async function createForgotPasswordChallenge(
  email: string,
): Promise<{ challengeId: string; step: string }> {
  const res = await client.post<{ challengeId: string; step: string }>(
    "/iam/api/v1/auth/challenge",
    { type: "reset_password", email },
  );
  return res.data;
}

export async function resetPassword(
  challengeId: string,
  password: string,
): Promise<void> {
  await client.post(`/iam/api/v1/auth/challenge/${challengeId}/set-password`, {
    password,
  });
}

export async function changePasswordByOld(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await client.post("/iam/api/v1/auth/change-password", {
    old_password: currentPassword,
    new_password: newPassword,
  });
}

/**
 * Sign this session out at IAM (204, idempotent; clears the auth cookies). Raw axios like the refresh call,
 * not `client`: a 401 here must not start a refresh + redirect to /login while the user is leaving.
 */
export async function signOut(): Promise<void> {
  await axios.post("/iam/api/v1/auth/sign-out", null, {
    withCredentials: true,
    headers: deviceIdHeaders(),
  });
}
